import {useEffect, useState} from 'react';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Link, useParams} from 'react-router-dom';
import {
  AttachmentModuleEnum,
  CourseClassroomStateEnum,
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {
  useGetCourseClassroomQuery,
  useGetCourseClassroomRosterQuery,
  useUpdateCourseClassroomMutation,
} from 'src/redux/queries/courseClassroom';
import {useGetCourseDefinitionQuery} from 'src/redux/queries/courseDefinition';
import {Spinner} from 'src/design/atoms/Spinner';
import {DetailGrid} from 'src/design/atoms/DetailGrid';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {InfoCard} from 'src/design/molecules/InfoCard';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {AttachmentsModule} from 'src/app/attachments/AttachmentsModule';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {ApiEndpoints} from 'src/shared/utils/ApiCaller';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {useStateTransitions} from 'src/shared/hooks/api/useStateTransitions';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import type {CourseClassroomViewType} from 'src/shared/constants/ViewTypes';
import {
  CourseClassroomPillConfig,
  TrainingIcon,
} from 'src/app/training/coursePills';
import {CreateDefaultCourseClassroom} from 'src/app/training/courseDefaults';
import {TrainingDocumentTitle} from 'src/app/training/courseContentValidation';
import {ClassCloseoutActions} from './components/ClassCloseoutActions';
import {ClassRosterTable} from './components/ClassRosterTable';
import {RosterBuilderModal} from './components/RosterBuilderModal';
import {ScheduleEditModal} from './components/ScheduleEditModal';
import {useClassRosterActions} from './components/useClassRosterActions';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

// Wave 4 transition name — mirrors backend CourseClassroomTransitionNames
// (Proact.Shared/Services/Courses/CourseClassroomStateMachine.cs). Not
// exposed on the generated client (state machine transitions are data, not
// typed contracts), so the literal is duplicated here deliberately. The
// complete/closeout/cancel transition names live in `ClassCloseoutActions`,
// the only other place that uses them.
const TRANSITION_START = 'start';

const UNDO_CLOSEOUT_WINDOW_HOURS = 24;

export const ClassWorkspacePage = () => {
  useSetDocumentTitle(TrainingDocumentTitle);
  const {definitionId, classroomId, view} = useParams();
  const trainingPerms = useModulePermissions(ModuleEnum.Training);
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();

  const [scheduleModalOpened, setScheduleModalOpened] = useState(false);

  const {
    data: definition,
    isLoading: isDefinitionLoading,
    error: definitionError,
  } = useGetCourseDefinitionQuery(parseInt(definitionId!), {
    skip: !definitionId,
  });

  const {
    data: classroomData,
    isLoading: isClassroomLoading,
    error: classroomError,
    refetch: refetchClassroom,
  } = useGetCourseClassroomQuery(parseInt(classroomId!), {
    skip: !classroomId,
  });

  const {
    data: rosterRows,
    isLoading: isRosterLoading,
    error: rosterError,
  } = useGetCourseClassroomRosterQuery(parseInt(classroomId!), {
    skip: !classroomId,
  });

  const [updateCourseClassroom, {isLoading: isSavingSchedule}] =
    useUpdateCourseClassroomMutation();

  const rosterActions = useClassRosterActions(classroomId);

  const [machine] = useStateMachine({
    url: ApiEndpoints.getCourseClassroomStateMachine,
  });

  const data =
    classroomData ?? CreateDefaultCourseClassroom(Number(definitionId ?? 0));
  const roster = rosterRows ?? [];

  const {availableTransitions, executeTransition, isTransitioning} =
    useStateTransitions({
      stateMachine: machine,
      stateId: data.state ?? CourseClassroomStateEnum.Invalid,
      transitionBaseUrl: `course-classroom/${classroomId}/transition`,
      module: ModuleEnum.Training,
      onTransitionComplete: () => refetchClassroom(),
      invalidateTagsOnComplete: ['CourseClassrooms', 'TaskAssignments'],
    });

  const findTransition = (name: string) =>
    availableTransitions.find(t => t.name === name);

  useEffect(() => {
    if (definitionError) {
      setAlert(parseErrorMessage(definitionError), 'error');
    }
  }, [definitionError]);

  useEffect(() => {
    if (classroomError) {
      setAlert(parseErrorMessage(classroomError), 'error');
    }
  }, [classroomError]);

  useEffect(() => {
    if (rosterError) {
      setAlert(parseErrorMessage(rosterError), 'error');
    }
  }, [rosterError]);

  const isLoading =
    isDefinitionLoading || isClassroomLoading || isRosterLoading;
  const hasError = definitionError || classroomError || rosterError;
  const isReady = !isLoading && !!classroomData;

  const canEdit =
    trainingPerms.update && data.state !== CourseClassroomStateEnum.Cancelled;
  const canEditRoster =
    canEdit &&
    (data.state === CourseClassroomStateEnum.NotStarted ||
      data.state === CourseClassroomStateEnum.InProgress);

  const closedAt = classroomData?.closedAt
    ? parseUtcDate(classroomData.closedAt)
    : undefined;
  const canUndoCloseout =
    trainingPerms.update &&
    closedAt !== undefined &&
    Date.now() - closedAt.getTime() <
      UNDO_CLOSEOUT_WINDOW_HOURS * 60 * 60 * 1000;

  const isCompleted = data.state === CourseClassroomStateEnum.Completed;

  // Completed but never closed out: "Complete Class" was used, or a close-out
  // failed part-way. No transition can reach it (all are From=InProgress), so
  // without this the roster can never be signed off.
  const canCloseOutLate =
    trainingPerms.update && isCompleted && closedAt === undefined;

  // Certificates only exist for attendees this class signed off, and only for
  // courses configured to generate them — matching what the backend enforces,
  // so the button is never a guaranteed no-op.
  const showIssueCertificates =
    trainingPerms.update &&
    isCompleted &&
    closedAt !== undefined &&
    definition?.isCertificateGenerated === true;

  const handleSaveSchedule = async (updates: {
    startsAt?: Date;
    endsAt?: Date;
    location?: string;
    instructorId?: number;
    capacity?: number;
    notes?: string;
  }) => {
    if (classroomData === undefined || definitionId === undefined) {
      return;
    }
    try {
      // Update still full-replaces attendees from assignmentIds via
      // MergeList — always send the current roster's assignment ids so a
      // schedule-only edit never touches attendance/result data.
      await updateCourseClassroom({
        id: classroomData.id,
        courseDefinitionId: parseInt(definitionId),
        courseDefinitionVersion: classroomData.courseDefinitionVersion,
        classroomAt: updates.startsAt ?? classroomData.classroomAt,
        assignmentIds: roster.map(row => row.assignmentId),
        ...updates,
      }).unwrap();
      setAlert('Schedule was successfully updated.', 'success');
      setScheduleModalOpened(false);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleStart = async () => {
    const transition = findTransition(TRANSITION_START);
    if (!transition) {
      return;
    }
    await executeTransition(transition);
  };

  const {breadcrumbs, fallbackTo, fallbackLabel} = useTrainingBreadcrumbs({
    mode: 'classroom',
    definitionId,
    courseName: definition?.name,
    classroomIdentifier: data?.internalIdentifier,
  });

  const pills = CourseClassroomPillConfig(data.state);

  const {RenderedTabsComponent} = useUrlQueryTabV2<CourseClassroomViewType>({
    view: (view ?? '') as CourseClassroomViewType,
    defaultView: 'roster',
    tabs: [
      {
        view: 'roster',
        label: 'Roster',
        component: (
          <DetailGrid>
            <DetailGrid.Main>
              <SectionCard
                title="Roster"
                rightSection={
                  canEditRoster && (
                    <PageHeader.Action
                      onClick={rosterActions.openRosterBuilder}>
                      Add Attendees
                    </PageHeader.Action>
                  )
                }>
                <Box p="md">
                  <ClassRosterTable
                    rows={roster}
                    canEdit={canEditRoster}
                    definitionId={String(definitionId)}
                    onAttendanceChange={rosterActions.handleAttendanceChange}
                    onResultChange={rosterActions.handleResultChange}
                    onRemove={rosterActions.handleRemoveAttendee}
                  />
                </Box>
              </SectionCard>
            </DetailGrid.Main>
            <DetailGrid.Sidebar>
              <InfoCard
                title="Schedule"
                rightSection={
                  canEdit && (
                    <PageHeader.Action
                      variant="edit"
                      onClick={() => setScheduleModalOpened(true)}>
                      Edit Schedule
                    </PageHeader.Action>
                  )
                }>
                <InfoCard.Row
                  label="Starts"
                  value={
                    data.startsAt
                      ? parseUtcDate(data.startsAt).toLocaleString()
                      : undefined
                  }
                />
                <InfoCard.Row
                  label="Ends"
                  value={
                    data.endsAt
                      ? parseUtcDate(data.endsAt).toLocaleString()
                      : undefined
                  }
                />
                <InfoCard.Row label="Location" value={data.location} />
                <InfoCard.Row
                  label="Instructor"
                  value={data.instructor?.name}
                />
                <InfoCard.Row
                  label="Capacity"
                  value={
                    data.capacity !== undefined
                      ? `${roster.length} / ${data.capacity}`
                      : `${roster.length} enrolled`
                  }
                />
                <InfoCard.Row label="Notes" value={data.notes} />
              </InfoCard>
            </DetailGrid.Sidebar>
          </DetailGrid>
        ),
      },
      {
        view: 'attachments',
        label: 'Attachments',
        component: (
          <>
            {data.id && (
              <AttachmentsModule
                entityId={data.id}
                attachmentType={AttachmentModuleEnum.CourseClassroom}
              />
            )}
          </>
        ),
      },
    ],
    onSelectView: nextView =>
      navigate(
        PathUtility.TrainingCourseClassroomView(
          definitionId as string,
          classroomId as string,
          nextView as any
        ),
        {replace: true}
      ),
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <Box>
        <BackBar
          fallbackTo={fallbackTo}
          fallbackLabel={fallbackLabel}
          breadcrumbs={breadcrumbs}
        />
        {hasError ? (
          <Alert color="red" title="Error">
            Failed to load classroom. Please try again later.
          </Alert>
        ) : !isReady ? (
          <Spinner loadingText="Loading...." />
        ) : (
          <>
            <PageHeader>
              <PageHeader.Title icon={TrainingIcon}>
                {definition?.name} Classroom {data?.internalIdentifier}
              </PageHeader.Title>
              {pills.length > 0 && (
                <PageHeader.Badges>
                  {pills.map((pill, index) => (
                    <PageHeader.Badge
                      key={index}
                      color={pill.color}
                      variant="light">
                      {pill.text}
                    </PageHeader.Badge>
                  ))}
                </PageHeader.Badges>
              )}
              <PageHeader.Actions>
                {data.state === CourseClassroomStateEnum.NotStarted &&
                  findTransition(TRANSITION_START) && (
                    <PageHeader.Action
                      onClick={handleStart}
                      loading={isTransitioning}>
                      Start Class
                    </PageHeader.Action>
                  )}
                {(data.state === CourseClassroomStateEnum.NotStarted ||
                  data.state === CourseClassroomStateEnum.InProgress) && (
                  <PageHeader.Action
                    component={Link}
                    to={PathUtility.TrainingCourseClassroomRun(
                      String(definitionId),
                      String(classroomId)
                    )}>
                    Day-of Mode
                  </PageHeader.Action>
                )}
                {classroomId && (
                  <ClassCloseoutActions
                    classroomId={classroomId}
                    findTransition={findTransition}
                    executeTransition={executeTransition}
                    isTransitioning={isTransitioning}
                    canUndoCloseout={canUndoCloseout}
                    canCloseOutLate={canCloseOutLate}
                    showIssueCertificates={showIssueCertificates}
                    instructorName={classroomData?.instructor?.name}
                    courseName={definition?.name}
                  />
                )}
              </PageHeader.Actions>
            </PageHeader>

            {RenderedTabsComponent}
          </>
        )}
      </Box>

      {classroomData && (
        <RosterBuilderModal
          opened={rosterActions.rosterBuilderOpened}
          onClose={rosterActions.closeRosterBuilder}
          courseDefinitionId={classroomData.courseDefinitionId}
          capacity={classroomData.capacity}
          currentRosterCount={roster.length}
          existingAssignmentIds={roster.map(row => row.assignmentId)}
          isSaving={rosterActions.isAddingAttendees}
          onAdd={rosterActions.handleAddAttendees}
        />
      )}

      {classroomData && (
        <ScheduleEditModal
          opened={scheduleModalOpened}
          onClose={() => setScheduleModalOpened(false)}
          classroom={classroomData}
          isSaving={isSavingSchedule}
          onSave={handleSaveSchedule}
        />
      )}
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.TrainingCourseClassroomView(
    ':definitionId',
    ':classroomId',
    ':view'
  ),
  element: <ClassWorkspacePage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
